import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { subjectClass } from "@/lib/subjects";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const SubjectBadge = ({ subject, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${subjectClass(subject)}`}>
    {subject}
  </span>
);

export const PageTitle = ({ title, subtitle, action }) => (
  <div className="mb-5 flex items-end justify-between gap-3 fade-up">
    <div>
      <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-1 text-sm font-medium text-muted-foreground">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const Card = ({ className = "", children, ...props }) => (
  <div className={`rounded-2xl border border-border/70 bg-card p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`} {...props}>
    {children}
  </div>
);

export const Empty = ({ icon: Icon, text, testId }) => (
  <div data-testid={testId} className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
    {Icon && <Icon className="h-6 w-6 opacity-60" />}
    {text}
  </div>
);

export const Loading = () => (
  <div className="flex justify-center py-10 text-muted-foreground" data-testid="loading-indicator">
    <Loader2 className="h-6 w-6 animate-spin" />
  </div>
);

export const ErrorBox = ({ text, onRetry }) => (
  <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm" data-testid="error-box">
    <p>{text}</p>
    {onRetry && <Button variant="outline" size="sm" className="mt-3" onClick={onRetry} data-testid="error-retry-btn">Riprova</Button>}
  </div>
);

export function FormSheet({ open, onOpenChange, title, description, children }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="mx-auto max-h-[90dvh] max-w-xl overflow-y-auto rounded-t-3xl pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
        <SheetHeader className="mb-4 text-left">
          <SheetTitle className="font-heading text-xl">{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  );
}

export function ConfirmDelete({ onConfirm, testId, label = "Elimina", title = "Eliminare questo elemento?" }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive" data-testid={testId} aria-label={label}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-sm rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>L'operazione non si può annullare.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>Annulla</AlertDialogCancel>
          <AlertDialogAction disabled={busy} onClick={run} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`${testId}-confirm`}>
            {label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
